import { useEffect, useMemo, useRef, useState } from 'react'
import { cn } from '@cocoonspace/ui/lib/utils'
import {
	eachDayOfInterval,
	endOfMonth,
	endOfWeek,
	format,
	isSameDay,
	isSameMonth,
	isToday,
	startOfMonth,
	startOfWeek,
} from 'date-fns'
import { enUS } from 'date-fns/locale/en-US'
import { fr } from 'date-fns/locale/fr'
import { EllipsisIcon } from 'lucide-react'

import { HiddenEventsBadge } from './hidden-events-badge'
import { useSchedulerCalendar } from './provider'
import type { SchedulerCalendarEvent } from './types'

type DayWithEvents = {
	day: Date
	events: SchedulerCalendarEvent[]
}

const buildMonthDays = (currentDate: Date, weekStartsOn: 0 | 1) => {
	const start = startOfWeek(startOfMonth(currentDate), { weekStartsOn })
	const end = endOfWeek(endOfMonth(currentDate), { weekStartsOn })

	return eachDayOfInterval({ end, start })
}

export const ViewMonth = () => {
	const {
		currentDate,
		events,
		selectedEventId,
		setSelectedEventId,
		locale,
		onSelectEvent,
	} = useSchedulerCalendar()

	const weekStartsOn: 0 | 1 = locale === 'en' ? 0 : 1
	const formatLocale = useMemo(() => (locale === 'en' ? enUS : fr), [locale])

	const days = useMemo(
		() => buildMonthDays(currentDate, weekStartsOn),
		[currentDate, weekStartsOn],
	)

	const daysWithEvents: DayWithEvents[] = useMemo(() => {
		return days.map((day) => {
			const dayEvents = events.filter((event) => {
				if (event.allDay) {
					return (
						day >= event.start &&
						day <= event.end &&
						!isSameDay(event.start, event.end)
					)
				}

				return isSameDay(day, event.start)
			})

			return { day, events: dayEvents }
		})
	}, [days, events])

	const weeks = useMemo(() => {
		const result: DayWithEvents[][] = []
		for (let i = 0; i < daysWithEvents.length; i += 7) {
			result.push(daysWithEvents.slice(i, i + 7))
		}
		return result
	}, [daysWithEvents])

	const weekdayLabels = useMemo(() => {
		const start = startOfWeek(currentDate, { weekStartsOn })
		const labels = eachDayOfInterval({
			end: endOfWeek(start, { weekStartsOn }),
			start,
		})

		return labels
	}, [currentDate, weekStartsOn])

	const [hiddenEventsCount, setHiddenEventsCount] = useState(0)
	const scrollContainerRef = useRef<HTMLDivElement | null>(null)
	const dayCellHeight = 130

	useEffect(() => {
		const container = scrollContainerRef.current
		if (!container) return

		const handleScroll = () => {
			const viewportBottom = container.scrollTop + container.clientHeight

			const totalHidden = weeks.reduce((sum, week, rowIndex) => {
				const rowTop = rowIndex * dayCellHeight
				if (rowTop < viewportBottom) return sum

				const hiddenInRow = week.reduce(
					(rowSum, dayWithEvents) => rowSum + dayWithEvents.events.length,
					0,
				)

				return sum + hiddenInRow
			}, 0)

			setHiddenEventsCount(totalHidden)
		}

		handleScroll()

		container.addEventListener('scroll', handleScroll)

		return () => {
			container.removeEventListener('scroll', handleScroll)
		}
	}, [weeks])

	return (
		<div className='relative flex flex-1 flex-col overflow-hidden'>
			<div
				className={cn(
					'border-border/70 bg-background/80 text-muted-foreground/70 sticky top-0 z-30 grid border-b text-xs uppercase backdrop-blur-md',
					'grid-cols-7',
				)}
			>
				{weekdayLabels.map((day) => (
					<div
						className='px-2 py-2 text-center'
						key={day.toString()}
					>
						{format(day, 'EEE', { locale: formatLocale })}
					</div>
				))}
			</div>

			<div
				className='flex-1 overflow-y-auto'
				ref={scrollContainerRef}
			>
				<div
					className='grid h-full'
					style={{
						gridTemplateRows: `repeat(${weeks.length}, minmax(0, 1fr))`,
					}}
				>
					{weeks.map((week) => (
						<div
							className='grid grid-cols-7'
							key={week[0]?.day.toString()}
						>
							{week.map(({ day, events: dayEvents }) => {
								const isCurrentMonth = isSameMonth(day, currentDate)
								const isTodayFlag = isToday(day)

								return (
									<div
										className='border-border/70 bg-background/60 flex h-[130px] flex-col border-r border-b px-1.5 pt-1.5 pb-1 text-xs last:border-r-0'
										data-today={isTodayFlag || undefined}
										key={day.toString()}
									>
										<div className='mb-1 flex items-center justify-between'>
											<span
												className={`inline-flex size-6 items-center justify-center rounded-full text-[11px] ${
													isTodayFlag
														? 'bg-primary/10 text-primary font-medium'
														: isCurrentMonth
															? 'text-foreground'
															: 'text-muted-foreground/60'
												}`}
											>
												{format(day, 'd', { locale: formatLocale })}
											</span>
										</div>

										<div className='flex flex-1 flex-col gap-0.5'>
											{dayEvents.slice(0, 3).map((event) => {
												const selected = selectedEventId === event.id

												return (
													<button
														data-scheduler-event='true'
														className={`bg-primary/10 text-foreground hover:bg-primary/15 w-full truncate rounded-md px-1.5 py-0.5 text-left text-[11px] ${
															selected ? 'ring-primary/40 ring-2' : ''
														}`}
														key={event.id}
														onClick={() => {
															setSelectedEventId(event.id)
															onSelectEvent?.(event)
														}}
														type='button'
													>
														<span className='truncate'>
															{event.title ||
																((event.metadata as any)?.location
																	?.name as string)}
														</span>
													</button>
												)
											})}

											{dayEvents.length > 3 && (
												<div className='text-muted-foreground mt-0.5 text-[11px]'>
													<EllipsisIcon />
												</div>
											)}
										</div>
									</div>
								)
							})}
						</div>
					))}
				</div>
			</div>

			<HiddenEventsBadge
				count={hiddenEventsCount}
				onClick={() => {
					const container = scrollContainerRef.current
					if (!container) return

					container.scrollTo({
						top: container.scrollHeight,
						behavior: 'smooth',
					})
				}}
			/>
		</div>
	)
}
